
import { MailIcon } from "@primer/octicons-react";
import { useReactToPrint } from "react-to-print";
import { useEffect, useRef, useState } from "react";
import Button from "../button";
import { Box } from "@primer/react";
import { eReducerStatusBase } from "../../state/reducer-model/eReducerStatusBase";
import { PlaceHolder } from "../place-holder";
import { useWindowSize } from "../../hooks/useWindowSize";

export interface IPrintPageConfig {
  // Document settings
  pageSize?: 'A4' | 'A5' | 'A3' | 'Letter' | string;
  isLandscape?: boolean,
  margins?: string;
  fontSize?: string;
  fontFamily?: string;
  
  // Print settings
  ignoreGlobalStyles?: boolean;
  removeAfterPrint?: boolean;
  copyStyles?: boolean;
  
  // New tab settings
  windowWidth?: number;
  windowHeight?: number;
  autoClose?: boolean;
  closeDelay?: number;
}

export interface IPrintPageProps {
  htmlTemplate: string;
  status?: eReducerStatusBase;
  title: string;
  
  // Event handlers
  onSendEmail?: () => void;
  onPrint?: () => void;
  
  // Configuration
  config?: IPrintPageConfig;
  
  // NEW: Option để mở tab mới
  openInNewTab?: boolean;
  
  // Custom buttons
  customButtons?: React.ReactNode[];
  
  // Labels
  printButtonText?: string;
  emailButtonText?: string;
  showEmailButton?: boolean;
  showPrintButton?: boolean;
  showPreview?: boolean;
}

const defaultConfig: IPrintPageConfig = {
  pageSize: 'A4',
  isLandscape: false,
  margins: '10mm 10mm',
  fontSize: '13pt',
  fontFamily: 'Times New Roman, sans-serif',
  ignoreGlobalStyles: true,
  removeAfterPrint: false,
  copyStyles: true,
  windowWidth: 900,
  windowHeight: 700,
  autoClose: true,
  closeDelay: 1500,
};

const PrintPage = ({
  htmlTemplate,
  status = eReducerStatusBase.is_completed,
  title,
  onSendEmail,
  onPrint,
  config = defaultConfig,
  openInNewTab = false,
  customButtons = [],
  printButtonText = "In biểu mẫu",
  emailButtonText = "Gửi Email",
  showEmailButton = true,
  showPrintButton = true,
  showPreview = false,
}: IPrintPageProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [printError, setPrintError] = useState<string>("");

  const finalConfig = { ...defaultConfig, ...config };
  
  const {height} = useWindowSize();
  // Generate page style for react-to-print
  const pageStyle = `
    @page {
      size: ${finalConfig.pageSize};
      margin: ${finalConfig.margins};
    }  
  `;

  // React-to-print function
  const reactToPrintFn = useReactToPrint({
    contentRef,
    documentTitle: title,
    pageStyle,
    // ignoreGlobalStyles: finalConfig.ignoreGlobalStyles,
    // removeAfterPrint: finalConfig.removeAfterPrint,
    // copyStyles: finalConfig.copyStyles,
    // onBeforePrint: () => {
    //   setPrintError("");
    // },
    onAfterPrint: () => {
    },
    onPrintError: (errorLocation, error) => {
      setPrintError(`Lỗi in: ${error.message}`);
    },
  });

  // Handle print with new tab option
  const handlePrint = () => {
    debugger
    if (onPrint) {
      onPrint();
    }

    if (!htmlTemplate || htmlTemplate.trim() === '') {
      setPrintError('Không có nội dung để in');
      return;
    }
    
   
      reactToPrintFn();
  };


  // Auto print effect
  useEffect(() => {
    if (!showPreview) {
        handlePrint();
    }
    }, []);

  const handleSendEmail = () => {
    if (onSendEmail) {
      onSendEmail();
    }
  };

  const isLoading = status === eReducerStatusBase.is_loading;
  const hasContent = htmlTemplate && htmlTemplate.trim() !== '';

  return (
    <Box sx={{ pt: 1 }}>
      {/* Preview content (optional) */}
      {showPreview && (
        <Box sx={{ mb: 1 }}>
          {isLoading && <PlaceHolder line_number={10} />}
          {hasContent && (
            <Box sx={{
              border: '1px solid',
              borderColor: 'border.default',
              borderRadius: 2,
              p: 0,
              maxHeight: `${height - 100}px`,
              overflow: 'auto',
              backgroundColor: 'white',
            }}>
              <div dangerouslySetInnerHTML={{ __html: htmlTemplate }} />
            </Box>
          )}
        </Box>
      )}

      {/* Hidden content for react-to-print */}
      {!openInNewTab && (
        <div style={{ display: 'none' }}>
          <div ref={contentRef}
          style={{
            fontFamily: finalConfig.fontFamily
          }}
          >
            <div dangerouslySetInnerHTML={{ __html: htmlTemplate }} />
          </div>
        </div>
      )}

      {/* Error message */}
      {printError && (
        <Box sx={{
          mb: 1,
          p: 0,
          backgroundColor: 'danger.subtle',
          border: '1px solid',
          borderColor: 'danger.border',
          borderRadius: 2
        }}>
          {printError}
        </Box>
      )}
      
      {/* Action buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        {showPrintButton && (
          <Button
            variant="primary"
            size="medium"
            text={printButtonText}
            onClick={handlePrint}
            disabled={!hasContent || isLoading}
            leadingVisual={() => <i className="fa fa-print" style={{ fontSize: 16 }} />}
          />
        )}
        
        {showEmailButton && onSendEmail && (
          <Button 
           size="medium"
            text={emailButtonText}
            onClick={handleSendEmail}
            disabled={!hasContent || isLoading}
            leadingVisual={MailIcon}
          />
        )}
        
        {customButtons}
      </Box>
    </Box>
  );
};

export default PrintPage;